import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { api } from '@/lib/api';
import type { Course } from '@/types';

export function CoursesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Get current user
  const { data: userData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['courses', statusFilter],
    queryFn: () =>
      api.getCourses(
        statusFilter !== 'all' ? { status: statusFilter } : undefined
      ),
  });

  const createCourseMutation = useMutation({
    mutationFn: (data: any) => api.createCourse(data),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      // Navigate to the course editor
      const courseId = result.course?.id;
      if (courseId) {
        navigate(`/author/courses/${courseId}/edit`);
      }
    },
    onError: (error) => {
      console.error('Failed to create course:', error);
      alert('Failed to create course. Please try again.');
    },
  });

  const handleCreateCourse = async () => {
    try {
      await createCourseMutation.mutateAsync({
        title: 'Untitled Course',
        description: '',
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const courses = ((data as any)?.courses || []) as Course[];

  const filteredCourses = courses.filter((course) =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
          <p className="text-gray-600 mt-1">
            Create and manage your learning content
          </p>
        </div>

        <button
          onClick={handleCreateCourse}
          disabled={createCourseMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          {createCourseMutation.isPending ? 'Creating...' : 'Create Course'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="review">In Review</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Courses Grid */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : filteredCourses.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-600 mb-4">No courses found</p>
          <button
            onClick={handleCreateCourse}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Create your first course
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <Link
              key={course.id}
              to={`/author/courses/${course.id}/edit`}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-primary-400 to-primary-600"></div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      course.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : course.status === 'review'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {course.status}
                  </span>
                </div>
                <h3 className="font-semibold text-lg mb-1">{course.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-2">
                  {course.description || 'No description'}
                </p>
                <div className="mt-4 text-xs text-gray-500">
                  Updated {new Date(course.updated_at * 1000).toLocaleDateString()}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
